import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarSvg } from "../../../svgFiles/CalendarSvg";
import "./customDatePicker.css";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const str = String(value).trim();
  if (!str) return null;

  if (str.includes("T")) {
    const parsed = new Date(str);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parts = str.split("-");
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      const [yyyy, mm, dd] = parts.map(Number);
      const parsed = new Date(yyyy, mm - 1, dd);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const [dd, mm, yyyy] = parts.map(Number);
    const parsed = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toValueString = (date, valueFormat) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  if (valueFormat === "dd-MM-yyyy") return `${dd}-${mm}-${yyyy}`;
  return `${yyyy}-${mm}-${dd}`;
};

const CustomDatePicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = "dd-mm-yyyy",
  dateFormat = "dd-MM-yyyy",
  valueFormat = "yyyy-MM-dd",
  className = "",
  inputClassName = "cp-input",
  minDate,
  maxDate,
  isClearable = true,
  showTodayButton = true,
  name,
  id,
  popperPlacement = "bottom-start",
}) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => toDate(value), [value]);

  const handleChange = (date) => {
    onChange?.(toValueString(date, valueFormat));
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onChange?.("");
    setOpen(false);
  };

  const handleToday = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    onChange?.(toValueString(today, valueFormat));
    setOpen(false);
  };

  return (
    <div className={`custom-datepicker ${disabled ? "is-disabled" : ""} ${className}`.trim()}>
      <DatePicker
        id={id}
        name={name}
        selected={selected}
        onChange={handleChange}
        disabled={disabled}
        open={open}
        onInputClick={() => !disabled && setOpen(true)}
        onClickOutside={() => setOpen(false)}
        onSelect={() => setOpen(false)}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        minDate={minDate}
        maxDate={maxDate}
        isClearable={false}
        showPopperArrow={false}
        popperPlacement={popperPlacement}
        popperClassName="custom-datepicker-popper"
        calendarClassName="custom-datepicker-calendar"
        className={`custom-datepicker__input ${inputClassName}`.trim()}
        wrapperClassName="custom-datepicker__wrapper"
        autoComplete="off"
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="custom-datepicker__header">
            <span className="custom-datepicker__month-label">
              {date.toLocaleString("en-US", { month: "long" })}, {date.getFullYear()}
            </span>
            <div className="custom-datepicker__nav">
              <button
                type="button"
                className="custom-datepicker__nav-btn"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                aria-label="Previous month"
              >
                ▲
              </button>
              <button
                type="button"
                className="custom-datepicker__nav-btn"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                aria-label="Next month"
              >
                ▼
              </button>
            </div>
          </div>
        )}
      >
        {(isClearable || showTodayButton) && (
          <div className="custom-datepicker__footer">
            {isClearable ? (
              <button type="button" className="custom-datepicker__footer-btn" onClick={handleClear}>
                Clear
              </button>
            ) : (
              <span />
            )}
            {showTodayButton ? (
              <button type="button" className="custom-datepicker__footer-btn" onClick={handleToday}>
                Today
              </button>
            ) : (
              <span />
            )}
          </div>
        )}
      </DatePicker>
      <span className="custom-datepicker__icon" aria-hidden="true">
        <CalendarSvg />
      </span>
    </div>
  );
};

export default CustomDatePicker;
